import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, SF_CO_APRV_SRCH, SG_CO_APRV_LIST, URLS } from "@/tra/tancis/ptli/usr/coaprv/services/PtliCoAprvService";

/*
 * Company Approval Request List
 * !== 업체 승인 요청 목록 ==!
 */
export const PTLI0915001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Company Approval Request List Search !== 업체 사용자 목록 검색 ==!
        coAprvRqstListSrch: useForm({
            defaultSchema: SF_CO_APRV_SRCH,
            defaultValues: {
                bsopPrcssStat: "U",
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Company Approval Request List !== 업체 사용자 목록 ==!
        coAprvRqstList: useGrid({
            defaultSchema: SG_CO_APRV_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Company Approval Request List !==  업체 사용자 목록 조회==!
        getCoAprvRqstList: useFetch({
            api: (page = grid.coAprvRqstList.page) =>
                APIS.getCoAprvRqstList(form.coAprvRqstListSrch.getValues(), page, grid.coAprvRqstList.size),
            enabled: comnUtils.isEmpty(form.coAprvRqstListSrch.errors),
            key: [grid.coAprvRqstList.page, grid.coAprvRqstList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.coAprvRqstListSrch.getValues(),
                    page: grid.coAprvRqstList.page,
                    size: grid.coAprvRqstList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };
    const render = {
        cell: {
            coNm: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/coaprv/ptli0915002s/${rowValues.xtrnUsrAplyId}`}>{value}</Link>;
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            grid.coAprvRqstList.setPage(page);
            fetch.getCoAprvRqstList.fetch(page);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.coAprvRqstListSrch.schema.coTin} />
                            <Group.Control {...form.coAprvRqstListSrch.schema.coNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.coAprvRqstListSrch.schema.usrNm} />
                            <Group.Control {...form.coAprvRqstListSrch.schema.bsopPrcssStat} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.coAprvRqstListSrch.schema.coTpCd} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.coAprvRqstListSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.coAprvRqstList.grid}
                            data={fetch.getCoAprvRqstList.data?.coAprvRqstList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
